import { useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  LinearProgress,
  InputBase,
  InputAdornment,
  Select,
  MenuItem,
  TextField,
} from '@mui/material';
import { GitBranch, Link as LinkIcon, Lock, RotateCcw, Search } from 'lucide-react';
import Marquee from 'react-fast-marquee';

import { useGitPage } from '../hooks/useGitPage';
import { GitTreeItem, GitFileContentViewer, GitLoginModal } from '../components';
import {
  SourceShell,
  SplitPane,
  Button as ShellButton,
  IconButton as ShellIconButton,
  EmptyState,
} from 'components/AppShell';
import { USE_NEW_SHELL } from 'core/flags';
import GitPageLegacy from './GitPage.legacy';

function GitPage() {
  const {
    url,
    setUrl,
    isAuthenticated,
    savedAccessToken,
    accessToken,
    setAccessToken,
    loginModalOpen,
    setLoginModalOpen,
    loading,
    loadingFiles,
    fileTree,
    branches,
    selectedBranch,
    selectedFiles,
    setSelectedFiles,
    selectedFile,
    selectedFileContent,
    loadingFileContent,
    isGitLoading,
    gitProgress,
    expandedPaths,
    toggleExpandPath,
    handleLoadCode,
    handleLoadFiles,
    handleFileSelect,
    handleFileCheckboxSelect,
    handleLogin,
    handleLogout,
    handleCloseModal,
    handleCancelLoadFiles,
    handleCancelIndex,
    handleSaveToContext,
    handleClearSelection,
    handleBranchChange,
    getSelectedFileContent,
    getLanguageForFile,
  } = useGitPage();

  const [urlDraft, setUrlDraft] = useState(url);
  const [branchQuery, setBranchQuery] = useState('');

  useEffect(() => {
    setUrlDraft(url);
  }, [url]);

  const filteredBranches = branchQuery.trim()
    ? branches.filter((b) => b.name.toLowerCase().includes(branchQuery.toLowerCase()))
    : branches;

  if (!USE_NEW_SHELL) {
    return <GitPageLegacy />;
  }

  const handleUrlCommit = () => {
    if (urlDraft.trim() && urlDraft !== url) setUrl(urlDraft.trim());
  };

  const selectedCount = selectedFiles.files.length;

  const urlInput = (
    <InputBase
      value={urlDraft}
      placeholder="https://github.com/user/repo"
      onChange={(e) => setUrlDraft(e.target.value)}
      onBlur={handleUrlCommit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          (e.target as HTMLInputElement).blur();
        }
      }}
      startAdornment={
        <InputAdornment position="start" sx={{ mr: 1, color: 'var(--shell-text-tertiary)' }}>
          <LinkIcon size={14} strokeWidth={1.75} />
        </InputAdornment>
      }
      sx={{
        flex: '1 1 480px',
        maxWidth: 560,
        minWidth: 280,
        height: 32,
        px: 1.25,
        fontSize: 13,
        background: 'var(--shell-bg-subtle)',
        border: '1px solid var(--shell-border-subtle)',
        borderRadius: 'var(--shell-radius-md)',
        '& input': { padding: 0, color: 'var(--shell-text-primary)' },
        '&.Mui-focused': {
          background: 'var(--shell-bg-surface)',
          borderColor: 'var(--shell-accent-primary)',
        },
      }}
    />
  );

  const branchPicker = (
    <Select<string>
      size="small"
      displayEmpty
      value={selectedBranch || ''}
      onChange={(e) => handleBranchChange(e.target.value)}
      disabled={branches.length === 0}
      onClose={() => setBranchQuery('')}
      MenuProps={{
        autoFocus: false,
        anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
        transformOrigin: { vertical: 'top', horizontal: 'right' },
        PaperProps: {
          sx: { mt: 0.5, width: 320, maxHeight: 420 },
        },
        sx: { zIndex: 1500 },
        MenuListProps: { sx: { paddingTop: 0 } },
      }}
      renderValue={(value) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GitBranch size={14} strokeWidth={1.75} color="var(--shell-text-tertiary)" />
          <span
            style={{
              color: value ? 'var(--shell-text-primary)' : 'var(--shell-text-tertiary)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {value || (branches.length === 0 ? 'Загрузите ветки' : 'Выбрать ветку')}
          </span>
        </Box>
      )}
      sx={{
        minWidth: 220,
        flexShrink: 0,
        height: 32,
        fontSize: 13,
        background: 'var(--shell-bg-surface)',
        '& .MuiSelect-select': {
          paddingY: 0,
          paddingX: 1.25,
          display: 'flex',
          alignItems: 'center',
        },
        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--shell-border-default)' },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: 'var(--shell-accent-primary)',
        },
      }}
    >
      {branches.length === 0
        ? [
            <MenuItem key="__empty" disabled value="">
              Сначала загрузите ветки
            </MenuItem>,
          ]
        : [
            <Box
              key="__search"
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 1,
                background: 'var(--shell-bg-surface)',
                borderBottom: '1px solid var(--shell-border-subtle)',
                p: 1,
              }}
              onKeyDown={(e) => e.stopPropagation()}
            >
              <TextField
                size="small"
                fullWidth
                autoFocus
                placeholder="Поиск ветки…"
                value={branchQuery}
                onChange={(e) => setBranchQuery(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={14} strokeWidth={1.75} color="var(--shell-text-tertiary)" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: 13,
                    backgroundColor: 'var(--shell-bg-subtle)',
                  },
                }}
              />
            </Box>,
            ...(filteredBranches.length === 0
              ? [
                  <MenuItem key="__nomatch" disabled value="">
                    Ничего не найдено
                  </MenuItem>,
                ]
              : filteredBranches.map((b) => (
                  <MenuItem key={b.name} value={b.name}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <GitBranch size={13} strokeWidth={1.75} color="var(--shell-text-tertiary)" />
                      <span style={{ flex: 1 }}>{b.name}</span>
                      {b.isDefault && (
                        <span
                          style={{
                            fontSize: 10,
                            fontWeight: 600,
                            color: 'var(--shell-accent-pressed)',
                            background: 'var(--shell-accent-subtle)',
                            padding: '1px 6px',
                            borderRadius: 4,
                            textTransform: 'uppercase',
                            letterSpacing: 0.5,
                          }}
                        >
                          default
                        </span>
                      )}
                    </Box>
                  </MenuItem>
                ))),
          ]}
    </Select>
  );

  const sidebarContent = (
    <>
      {/* Управление загрузкой */}
      <div
        style={{
          padding: 12,
          borderBottom: '1px solid var(--shell-border-subtle)',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          flexShrink: 0,
        }}
      >
        <ShellButton
          variant="secondary"
          size="sm"
          fullWidth
          onClick={handleLoadCode}
          disabled={loading || !isAuthenticated || !url.trim()}
        >
          {loading
            ? 'Загрузка веток…'
            : branches.length > 0
              ? 'Перезагрузить ветки'
              : 'Загрузить ветки'}
        </ShellButton>
        {loadingFiles ? (
          <div style={{ display: 'flex', gap: 6 }}>
            <ShellButton variant="primary" size="sm" fullWidth disabled>
              Загрузка файлов…
            </ShellButton>
            <ShellButton variant="danger" size="sm" onClick={handleCancelLoadFiles}>
              Отмена
            </ShellButton>
          </div>
        ) : (
          <ShellButton
            variant="primary"
            size="sm"
            fullWidth
            onClick={handleLoadFiles}
            disabled={loading || branches.length === 0 || !selectedBranch}
          >
            Загрузить файлы
          </ShellButton>
        )}
      </div>

      {/* Дерево */}
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        {loading ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 200,
            }}
          >
            <CircularProgress size={20} sx={{ color: 'var(--shell-accent-primary)' }} />
          </div>
        ) : !isAuthenticated ? (
          <EmptyState
            size="sm"
            icon={<Lock size={18} strokeWidth={1.75} />}
            title="Требуется авторизация"
            description="Подключите токен Git, чтобы загрузить дерево репозитория."
            action={
              <ShellButton variant="primary" size="sm" onClick={() => setLoginModalOpen(true)}>
                Подключить токен
              </ShellButton>
            }
          />
        ) : fileTree.length === 0 ? (
          <EmptyState
            size="sm"
            title="Дерево пусто"
            description="Укажите URL и нажмите «Загрузить файлы»."
          />
        ) : (
          <div style={{ padding: '4px 0' }}>
            {fileTree.map((item, index) => (
              <GitTreeItem
                key={`${item.name}-${index}`}
                item={item}
                level={0}
                path=""
                handleFileSelect={handleFileSelect}
                setSelectedFiles={setSelectedFiles}
                selectedFiles={selectedFiles}
                onFileCheckboxSelect={handleFileCheckboxSelect}
                expandedPaths={expandedPaths}
                toggleExpandPath={toggleExpandPath}
                disabled={isGitLoading}
              />
            ))}
          </div>
        )}
      </div>

      {/* Прогресс индексирования */}
      {isGitLoading && gitProgress?.value != null && (
        <div
          style={{
            padding: 10,
            borderTop: '1px solid var(--shell-border-subtle)',
            background: 'var(--shell-bg-subtle)',
            flexShrink: 0,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 4,
              fontSize: 11,
            }}
          >
            <Marquee
              loop={1}
              style={{
                flex: 1,
                overflow: 'hidden',
                color: 'var(--shell-text-secondary)',
              }}
            >
              {gitProgress.label ?? ''}
            </Marquee>
            <span style={{ color: 'var(--shell-accent-pressed)', fontWeight: 600 }}>
              {gitProgress.value != null
                ? parseFloat(Number(gitProgress.value).toFixed(2)).toString()
                : '0'}
              %
            </span>
          </div>
          <LinearProgress
            variant="determinate"
            value={gitProgress.value ?? 0}
            sx={{
              height: 4,
              borderRadius: 2,
              backgroundColor: 'var(--shell-bg-hover)',
              '& .MuiLinearProgress-bar': {
                backgroundColor: 'var(--shell-accent-primary)',
                borderRadius: 2,
              },
            }}
          />
        </div>
      )}
    </>
  );

  const viewerContent = (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      {!isAuthenticated ? (
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <EmptyState
            icon={<Lock size={22} strokeWidth={1.5} />}
            title="Требуется авторизация"
            description="Подключите токен Git, чтобы просматривать файлы репозитория."
            action={
              <ShellButton variant="primary" size="sm" onClick={() => setLoginModalOpen(true)}>
                Подключить токен
              </ShellButton>
            }
          />
        </div>
      ) : selectedFile ? (
        <>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '10px 16px',
              borderBottom: '1px solid var(--shell-border-subtle)',
              background: 'var(--shell-bg-surface)',
              flexShrink: 0,
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: 'var(--shell-text-primary)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {selectedFile.name}
              </div>
              <div style={{ fontSize: 11, color: 'var(--shell-text-tertiary)' }}>
                {getLanguageForFile(selectedFile.name).toUpperCase()}
                {' · '}
                {selectedBranch}
              </div>
            </div>
          </div>
          <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
            <GitFileContentViewer
              selectedFile={selectedFile}
              selectedFileContent={selectedFileContent}
              loadingFileContent={loadingFileContent}
              selectedBranch={selectedBranch}
              getLanguageForFile={getLanguageForFile}
              getSelectedFileContent={getSelectedFileContent}
              hideHeader
            />
          </div>
        </>
      ) : (
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <EmptyState
            title="Файл не выбран"
            description="Выберите файл слева, чтобы открыть его содержимое."
          />
        </div>
      )}
    </div>
  );

  return (
    <>
      <SourceShell
        title="Git репозиторий"
        subtitle="Подключите репозиторий и выберите файлы для контекста"
        toolbar={urlInput}
        actions={
          <>
            {branchPicker}
            <ShellIconButton
              label="Перезагрузить ветки"
              onClick={handleLoadCode}
              disabled={loading || !isAuthenticated || !url.trim()}
              icon={<RotateCcw size={14} strokeWidth={1.75} />}
            />
          </>
        }
        connection={{
          status: isAuthenticated ? 'connected' : 'disconnected',
          label: 'Git',
          hint: isAuthenticated ? 'GitHub Access Token сохранён' : undefined,
          onConnect: () => setLoginModalOpen(true),
          onDisconnect: handleLogout,
          connectLabel: 'Подключить токен',
        }}
        selection={
          selectedCount > 0
            ? {
                count: selectedCount,
                actions: (
                  <>
                    {isGitLoading ? (
                      <ShellButton variant="danger" onClick={handleCancelIndex}>
                        Отменить индексацию
                      </ShellButton>
                    ) : (
                      <>
                        <ShellButton variant="ghost" onClick={handleClearSelection}>
                          Сбросить
                        </ShellButton>
                        <ShellButton
                          variant="primary"
                          leftIcon={<GitBranch size={14} strokeWidth={1.75} />}
                          onClick={handleSaveToContext}
                          disabled={isGitLoading}
                        >
                          Сохранить в контекст
                        </ShellButton>
                      </>
                    )}
                  </>
                ),
              }
            : undefined
        }
      >
        <SplitPane left={sidebarContent} right={viewerContent} leftWidth={320} />
      </SourceShell>

      <GitLoginModal
        open={loginModalOpen}
        onClose={handleCloseModal}
        isAuthenticated={isAuthenticated}
        savedAccessToken={savedAccessToken}
        accessToken={accessToken}
        setAccessToken={setAccessToken}
        onLogin={handleLogin}
        onLogout={handleLogout}
      />
    </>
  );
}

export default GitPage;
